import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, Paper, Typography, Button, Box, 
  Divider, CircularProgress, Alert, Tabs, Tab,
  Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText,
  TextField, Grid, FormControl, InputLabel, Select, MenuItem,
  IconButton, Tooltip, Card, CardContent, CardActions, Accordion,
  AccordionSummary, AccordionDetails, Snackbar, Menu,
  ListItemIcon, ListItemText, Chip, ListItem, List, Stack,
  AppBar, Toolbar, useTheme, alpha
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import TableChartIcon from '@mui/icons-material/TableChart';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DescriptionIcon from '@mui/icons-material/Description';
import { generateSpecification, generateWithTemplate, uploadImage, saveMarkdown, downloadDocument, getDocumentByDocumentId } from '../utils/api';
import { downloadDocumentById, createDocument } from '../utils/documentService';
import TableComponents from '../components/TableComponents';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import CodeIcon from '@mui/icons-material/Code';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BugReportIcon from '@mui/icons-material/BugReport';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ListSubheader from '@mui/material/ListSubheader';
import Collapse from '@mui/material/Collapse';
import { enhanceSectionWithAI } from '../utils/api';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import DocumentTags from '../components/DocumentTags';
import DocumentProjectAccess from '../components/DocumentProjectAccess';
import { useAuth } from '../contexts/AuthContext';

const ViewDocumentPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [document, setDocument] = useState('');
  const [originalAIContent, setOriginalAIContent] = useState('');
  const [documentSections, setDocumentSections] = useState([]);
  const [documentFormat, setDocumentFormat] = useState('md');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('preview');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [sectionEditDialogOpen, setSectionEditDialogOpen] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(-1);
  const [currentSectionContent, setCurrentSectionContent] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [updatingDocument, setUpdatingDocument] = useState(false);
  const [aiToolMenuAnchor, setAiToolMenuAnchor] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiProcessing, setAIProcessing] = useState(false);
  const [aiMenuAnchor, setAiMenuAnchor] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiSuggestionsOpen, setAiSuggestionsOpen] = useState(false);
  const [documentSaved, setDocumentSaved] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [savingDocument, setSavingDocument] = useState(false);

  const [documentTags, setDocumentTags] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const savedDocumentId = sessionStorage.getItem('saved_document_id');
  
  // Create a ref for the section editor textarea
  const sectionEditorRef = useRef(null);
  
  // Table editor state
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [rows, setRows] = useState(2);
  const [columns, setColumns] = useState(2);
  const [tableHeaders, setTableHeaders] = useState([]);
  const [tableContent, setTableContent] = useState([]);
  const [tableStyle, setTableStyle] = useState('default');
  
  // Image upload state
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageDescription, setImageDescription] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  
  // Form data for editing
  const [formData, setFormData] = useState({
    project_type: '',
    author_name: '',
    date: new Date().toISOString().split('T')[0],
    project_name: '',
    client: '',
    custom_prompt: '',
    attachments: []
  });

  const aiFeatureInfo = {
    enhance: {
      title: "Enhance Section",
      description: "Improves quality, structure, and technical details of the current section with SAP-specific terminology and professional consulting language.",
      usage: "Use when your content needs polishing or more technical depth.",
      expectation: "Enhanced content with better formatting and more SAP-specific details."
    },
    suggest: {
      title: "AI Suggestions",
      description: "Generates a list of content recommendations that you can selectively add to your section.",
      usage: "Use when you're not sure what else to include or want to explore options.",
      expectation: "Multiple specific suggestions that you can add individually with a single click."
    },
    requirements: {
      title: "Generate Additional Requirements",
      description: "Creates new business or functional requirements tailored to your SAP project.",
      usage: "Use in Requirements sections to ensure comprehensive coverage.",
      expectation: "Professionally formatted requirements with clear descriptions."
    },
    criteria: {
      title: "Add Acceptance Criteria",
      description: "Generates testable criteria for your existing requirements.",
      usage: "Use after defining requirements to make them more measurable and testable.",
      expectation: "Clear, specific acceptance criteria that define when a requirement is met."
    },
    config: {
      title: "Add SAP Configuration",
      description: "Creates SAP-specific configuration steps with IMG paths and settings.",
      usage: "Use in technical sections where you need to explain SAP setup details.",
      expectation: "Detailed IMG paths, transaction codes, and configuration parameters."
    },
    code: {
      title: "Generate ABAP Code",
      description: "Creates SAP ABAP code snippets relevant to your section.",
      usage: "Use in development-related sections to show implementation examples.",
      expectation: "Syntactically correct ABAP code with comments explaining functionality."
    },
    diagram: {
      title: "Create Process Diagram",
      description: "Generates a text-based process flow diagram in your document.",
      usage: "Use to visually represent workflows, processes, or system interactions.",
      expectation: "ASCII/text-based diagram showing process steps and relationships."
    },
    technical: {
      title: "Generate Technical Content",
      description: "Adds SAP-specific technical details like tables, BAPIs, and RFC calls.",
      usage: "Use when you need to enhance any section with more technical depth.",
      expectation: "Technical references, SAP object names, and implementation details."
    },
    expand: {
      title: "Expand Selected Text",
      description: "Takes your selected text and elaborates on it with more details.",
      usage: "Use when you have a brief point that needs more explanation.",
      expectation: "An expanded version of your selection with supporting details."
    },
    examples: {
      title: "Add Examples",
      description: "Generates relevant examples for the concepts in your section.",
      usage: "Use to make abstract concepts more concrete and understandable.",
      expectation: "Practical examples related to your SAP implementation."
    },
    test: {
      title: "Generate Test Scenarios",
      description: "Creates test scenarios based on your requirements or functionality.",
      usage: "Use in testing sections to define how features should be tested.",
      expectation: "Comprehensive test cases with steps, expected results, and test data."
    },
    risk: {
      title: "Add Risk Assessment",
      description: "Identifies potential risks and mitigation strategies.",
      usage: "Use in risk management sections to ensure thorough risk coverage.",
      expectation: "Structured risk items with impact, likelihood, and mitigation approaches."
    }
  };
  
  // Parse document into sections
  // All required SAP Functional Specification sections including document header sections
  const requiredSections = [
    "Document Information",
    "Document Revision History", 
    "Approvals",
    "General Information",
    "Contact Information",
    "Executive Summary",
    "Current Process",
    "Future Process", 
    "Business Requirements",
    "System Requirements",
    "Integration Requirements",
    "Technical Design",
    "Implementation Strategy",
    "Risk Assessment",
    "Testing Strategy",
    "Custom Development",
    "User Interface",
    "Processing Logic",
    "Training and Documentation",
    "Post-Implementation Support",
    "Appendices"
  ];

  // Default section templates for missing sections
  const defaultSections = [
    { title: 'Document Information', content: '| Field | Value |\n| --- | --- |\n| Document ID | DOCID-001 |\n| Document Name | SAP Functional Specification |\n| Document Version | 1.0 |\n| Project Name | [Project Name] |\n| Client | [Client Name] |\n| Created By | [Author Name] |\n| Created Date | [Date] |\n| Status | Draft |\n| Document Type | SAP Functional Specification |' },
    { title: 'Document Revision History', content: '| Version | Date | Author | Description of Changes |\n| --- | --- | --- | --- |\n| 1.0 | [Date] | [Author] | Initial document creation |\n\n(Edit this section to track document revisions)' },
    { title: 'Approvals', content: '| Name | Role | Approval Date | Status |\n| --- | --- | --- | --- |\n| | | | |\n\n(Edit this section to add approvers and their status)' },
    { title: 'Purpose', content: 'This document provides the detailed functional specifications for the [Project Type] project. It outlines the scope, objectives, technical requirements, and implementation approach to ensure alignment with business needs.' },
    { title: 'General Information', content: 'This project involves the implementation of SAP S/4HANA to streamline business processes, improve operational efficiency, and enhance data visibility. The project aims to replace legacy systems with a modern integrated solution to support end-to-end business operations.' },
    { title: 'Contact Information', content: '| Name | Role | Organization | Email | Phone |\n| --- | --- | --- | --- | --- |\n| | | | | |\n\n(Edit this section to add key stakeholders and their contact details)' }
  ];

  const parseDocumentSections = useCallback((content) => {
    if (!content) {
      // Return default sections if no content
      return [...defaultSections];
    }
    
    const lines = content.split('\n');
    const sections = [];
    let currentSection = null;
    let currentContent = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      if (line.startsWith('# ')) {
        // If we already have a section, save it
        if (currentSection) {
          sections.push({
            title: currentSection,
            content: currentContent.join('\n')
          });
        }
        
        // Start a new section
        currentSection = line.substring(2).trim();
        currentContent = [];
      } else if (line.startsWith('## ') && !currentSection) {
        // Handle case where document might start with a level 2 heading
        currentSection = line.substring(3).trim();
        currentContent = [];
      } else {
        // Add line to current section content
        if (currentSection) {
          currentContent.push(line);
        } else if (line.trim() !== '') {
          // Document starts with content before any heading
          currentSection = 'Introduction';
          currentContent.push(line);
        }
      }
    }
    
    // Add the last section
    if (currentSection) {
      sections.push({
        title: currentSection,
        content: currentContent.join('\n')
      });
    }
    
    // Validate sections and ensure all required sections are present
    const validatedSections = validateRequiredSections(sections);
    
    return validatedSections;
  }, []);

  // Function to validate and ensure all required sections are present
  const validateRequiredSections = useCallback((sections) => {
    const foundSections = new Map();
    
    // Store found sections
    sections.forEach(section => {
      foundSections.set(section.title, section);
    });
    
    // Build complete sections list with required sections
    const completeSections = [];
    
    // First add all required sections (in order)
    requiredSections.forEach(requiredTitle => {
      if (foundSections.has(requiredTitle)) {
        completeSections.push(foundSections.get(requiredTitle));
      } else {
        // Add placeholder for missing required section
        completeSections.push({
          title: requiredTitle,
          content: `[This section needs to be completed]\n\nPlease provide detailed content for the ${requiredTitle} section based on your project requirements.`,
          isMissing: true
        });
        console.warn(`Missing required section: ${requiredTitle}`);
      }
    });
    
    // Then add any additional sections that were found but not in required list
    sections.forEach(section => {
      if (!requiredSections.includes(section.title)) {
        completeSections.push(section);
      }
    });
    
    return completeSections;
  }, [requiredSections]);
  
  // Reconstruct document from sections
  const reconstructDocument = useCallback((sections) => {
    return sections.map(section => {
      return `# ${section.title}\n${section.content}`;
    }).join('\n\n');
  }, []);
  
  // Function to load document tags from backend
  const loadDocumentTags = async (documentId) => {
    try {
      const result = await getDocumentByDocumentId(documentId);
      if (result.success && result.document && result.document.tags) {
        setDocumentTags(result.document.tags);
      } else {
        // If no tags exist yet, create default tags
        await createDefaultTags(documentId);
      }
    } catch (error) {
      console.error('Error loading document tags:', error);
      // If loading fails, try to create default tags
      await createDefaultTags(documentId);
    }
  };
  
  // Function to create default tags for a document (fallback if backend didn't create them)
  const createDefaultTags = async (documentId) => {
    try {
      // This function is a fallback in case the backend didn't create default tags
      // The backend should automatically create: Author, Client/Project tags only
      console.log('Creating fallback default tags for document:', documentId);
      
      const { addDocumentTags } = await import('../utils/documentService');
      const defaultTags = [];
      
      // Add Author tag
      if (currentUser?.full_name) {
        defaultTags.push({
          name: currentUser.full_name,
          color: '#2196F3'
        });
      }
      
      // Add Client tag (prioritize client over project name)
      if (formData.client) {
        defaultTags.push({
          name: formData.client,
          color: '#F5821F'
        });
      } else if (formData.project_name) {
        // If no client specified, use project name as client
        defaultTags.push({
          name: formData.project_name,
          color: '#F5821F'
        });
      }
      
      if (defaultTags.length > 0) {
        const tagResult = await addDocumentTags(documentId, defaultTags);
        if (tagResult.success) {
          setDocumentTags(tagResult.tags || []);
        }
      }
    } catch (error) {
      console.error('Error creating default tags:', error);
    }
  };
  
  useEffect(() => {
    const storedDocument = sessionStorage.getItem('generatedDocument');
    const storedOriginalContent = sessionStorage.getItem('originalAIContent');
    const storedFormat = sessionStorage.getItem('documentFormat') || 'md';
    const storedFormData = sessionStorage.getItem('formData');
    const storedAiPrompt = sessionStorage.getItem('aiPrompt');
    const documentSavedState = sessionStorage.getItem('documentSaved') === 'true';

    const storedProjectId = sessionStorage.getItem('projectId');
    if (storedProjectId) {
      setProjectId(storedProjectId);
    }
    
    if (!storedDocument) {
      setError('No document found. Please generate a new specification.');
      setLoading(false);
      return;
    }
    
    setDocument(storedDocument);
    setOriginalAIContent(storedOriginalContent || storedDocument);
    setDocumentFormat(storedFormat);
    setDocumentSaved(documentSavedState);
    
    // Parse document into sections
    const sections = parseDocumentSections(storedDocument);
    setDocumentSections(sections);
    
    if (storedFormData) {
      try {
        setFormData(JSON.parse(storedFormData));
      } catch (e) {
        console.error('Error parsing stored form data', e);
      }
    }
    
    if (storedAiPrompt) {
      try {
        setAiPrompt(storedAiPrompt);
      } catch (e) {
        console.error('Error parsing stored AI prompt', e);
      }
    }
    
    // Load document tags if available - only initialize for saved documents
    if (savedDocumentId) {
      // For saved documents, load existing tags from backend
      loadDocumentTags(savedDocumentId);
    } else {
      // For new/unsaved documents, don't show tags yet
      setDocumentTags([]);
    }
    
    setLoading(false);
  }, [parseDocumentSections, currentUser]);
  
  const handleViewModeChange = (event, newValue) => {
    setViewMode(newValue);
  };
  
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleEditDialogOpen = () => {
    setEditDialogOpen(true);
  };
  
  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
  };
  
  const handleSectionEditOpen = (index) => {
    setCurrentSectionIndex(index);
    setCurrentSectionContent(documentSections[index].content);
    setSectionEditDialogOpen(true);
  };
  
  const handleSectionEditClose = () => {
    setSectionEditDialogOpen(false);
    setCurrentSectionIndex(-1);
    setCurrentSectionContent('');
  };
  
  const handleSectionContentChange = (e) => {
    setCurrentSectionContent(e.target.value);
  };
  
  const handleSectionSave = () => {
    if (currentSectionIndex < 0) return;
    
    // Update the section
    const updatedSections = [...documentSections];
    updatedSections[currentSectionIndex] = {
      ...updatedSections[currentSectionIndex],
      content: currentSectionContent
    };
    
    setDocumentSections(updatedSections);
    
    // Reconstruct the document
    const updatedDocument = reconstructDocument(updatedSections);
    setDocument(updatedDocument);
    sessionStorage.setItem('generatedDocument', updatedDocument);
    
    // Close the dialog
    handleSectionEditClose();
    
    // Show success message
    setSnackbarMessage('Section updated successfully');
    setSnackbarOpen(true);
  };
  
  const handleUpdateDocument = async () => {
    setUpdatingDocument(true);
    
    try {
      // Generate new content
      const aiResponse = await generateSpecification(formData);
      
      if (!aiResponse.success) {
        setError('Failed to update document. Please try again.');
        setEditDialogOpen(false);
        return;
      }
      
      // Store updated form data and original AI content
      sessionStorage.setItem('formData', JSON.stringify(formData));
      sessionStorage.setItem('originalAIContent', aiResponse.document);
      
      // Store the AI prompt for the Prompt tab
      if (aiResponse.prompt) {
        setAiPrompt(JSON.stringify(aiResponse.prompt, null, 2));
        sessionStorage.setItem('aiPrompt', JSON.stringify(aiResponse.prompt, null, 2));
      }
      
      // If a template is selected, apply it
      if (selectedTemplateId) {
        const templateResponse = await generateWithTemplate(selectedTemplateId, aiResponse.document);
        
        if (templateResponse.success) {
          const newDocument = templateResponse.document;
          setDocument(newDocument);
          sessionStorage.setItem('generatedDocument', newDocument);
          sessionStorage.setItem('documentFormat', templateResponse.format || 'md');
          
          // Parse the new document into sections
          const newSections = parseDocumentSections(newDocument);
          setDocumentSections(newSections);
        } else {
          setError('Failed to apply template. Using raw content instead.');
          const newDocument = aiResponse.document;
          setDocument(newDocument);
          sessionStorage.setItem('generatedDocument', newDocument);
          sessionStorage.setItem('documentFormat', 'md');
          
          // Parse the new document into sections
          const newSections = parseDocumentSections(newDocument);
          setDocumentSections(newSections);
        }
      } else {
        // No template selected, use raw AI output
        const newDocument = aiResponse.document;
        setDocument(newDocument);
        sessionStorage.setItem('generatedDocument', newDocument);
        sessionStorage.setItem('documentFormat', 'md');
        
        // Parse the new document into sections
        const newSections = parseDocumentSections(newDocument);
        setDocumentSections(newSections);
      }
      
      setViewMode('preview');
      setEditDialogOpen(false);
    } catch (err) {
      console.error('Error updating document:', err);
      setError(err.response?.data?.error || 'An error occurred. Please try again.');
    } finally {
      setUpdatingDocument(false);
    }
  };
  
  const handleAIToolMenuOpen = (event) => {
    setAiToolMenuAnchor(event.currentTarget);
  };
  
  const handleAIToolMenuClose = () => {
    setAiToolMenuAnchor(null);
  };
  
  // Table dialog handlers
  const handleTableDialogOpen = () => {
    // Initialize table structure
    setTableHeaders(Array(columns).fill(''));
    setTableContent(Array(rows).fill().map(() => Array(columns).fill('')));
    setTableStyle('default');
    setTableDialogOpen(true);
  };
  
  const handleTableDialogClose = () => {
    setTableDialogOpen(false);
  };
  
  const handleRowColumnChange = (e) => {
    const { name, value } = e.target;
    const numValue = Math.max(1, parseInt(value) || 1);
    
    if (name === 'rows') {
      setRows(numValue);
      // Update table content when rows change
      if (numValue > tableContent.length) {
        // Add rows
        const newRows = Array(numValue - tableContent.length).fill().map(() => Array(columns).fill(''));
        setTableContent([...tableContent, ...newRows]);
      } else {
        // Remove rows
        setTableContent(tableContent.slice(0, numValue));
      }
    } else if (name === 'columns') {
      setColumns(numValue);
      // Update headers and each row when columns change
      setTableHeaders(prev => {
        if (numValue > prev.length) {
          // Add columns to headers
          return [...prev, ...Array(numValue - prev.length).fill('')];
        } else {
          // Remove columns from headers
          return prev.slice(0, numValue);
        }
      });
      
      // Update each row
      setTableContent(prev => prev.map(row => {
        if (numValue > row.length) {
          // Add columns to row
          return [...row, ...Array(numValue - row.length).fill('')];
        } else {
          // Remove columns from row
          return row.slice(0, numValue);
        }
      }));
    }
  };
  
  const handleHeaderChange = (index, value) => {
    setTableHeaders(prev => {
      const newHeaders = [...prev];
      newHeaders[index] = value;
      return newHeaders;
    });
  };
  
  const handleCellChange = (rowIndex, colIndex, value) => {
    setTableContent(prev => {
      const newContent = [...prev];
      newContent[rowIndex] = [...newContent[rowIndex]];
      newContent[rowIndex][colIndex] = value;
      return newContent;
    });
  };
  
  const handleTableStyleChange = (e) => {
    setTableStyle(e.target.value);
  };
  
  const handleAddRow = () => {
    setRows(rows + 1);
    setTableContent([...tableContent, Array(columns).fill('')]);
  };
  
  const handleAddColumn = () => {
    setColumns(columns + 1);
    setTableHeaders([...tableHeaders, '']);
    setTableContent(tableContent.map(row => [...row, '']));
  };
  
  const handleDeleteRow = (rowIndex) => {
    if (tableContent.length > 1) {
      const newContent = [...tableContent];
      newContent.splice(rowIndex, 1);
      setTableContent(newContent);
      setRows(rows - 1);
    }
  };
  
  const handleDeleteColumn = (colIndex) => {
    if (tableHeaders.length > 1) {
      const newHeaders = [...tableHeaders];
      newHeaders.splice(colIndex, 1);
      setTableHeaders(newHeaders);
      
      const newContent = tableContent.map(row => {
        const newRow = [...row];
        newRow.splice(colIndex, 1);
        return newRow;
      });
      
      setTableContent(newContent);
      setColumns(columns - 1);
    }
  };
  
  const handleInsertTable = () => {
    // Generate rich table HTML instead of markdown
    let tableHtml = `<table class="table-${tableStyle}" style="width:100%; border-collapse:collapse; margin-bottom:15px;">`;
    
    // Add headers
    tableHtml += '<thead><tr>';
    tableHeaders.forEach(header => {
      tableHtml += `<th style="padding:8px; border:1px solid #ddd; background-color:#f5f5f5; font-weight:bold;">${header || ' '}</th>`;
    });
    tableHtml += '</tr></thead>';
    
    // Add content rows
    tableHtml += '<tbody>';
    tableContent.forEach(row => {
      tableHtml += '<tr>';
      row.forEach(cell => {
        tableHtml += `<td style="padding:8px; border:1px solid #ddd;">${cell || ' '}</td>`;
      });
      tableHtml += '</tr>';
    });
    tableHtml += '</tbody></table>';
    
    // Convert the HTML to markdown-compatible format
    // For now, we'll still use markdown tables since that's what the document expects
    let tableMarkdown = '';
    
    // Add headers
    tableMarkdown += '| ' + tableHeaders.map(header => header || ' ').join(' | ') + ' |\n';
    // Add separator row
    tableMarkdown += '| ' + tableHeaders.map(() => '---').join(' | ') + ' |\n';
    // Add content rows
    tableContent.forEach(row => {
      tableMarkdown += '| ' + row.map(cell => cell || ' ').join(' | ') + ' |\n';
    });
    
    // Insert the table at cursor position or replace selection
    const selection = window.getSelection().toString();
    let newContent = currentSectionContent;
    
    if (selection) {
      newContent = currentSectionContent.replace(selection, tableMarkdown);
    } else {
      // Get cursor position from ref
      let cursorPos = 0;
      if (sectionEditorRef.current) {
        cursorPos = sectionEditorRef.current.selectionStart || 0;
      }
      
      newContent = currentSectionContent.substring(0, cursorPos) + 
                  tableMarkdown +
                  currentSectionContent.substring(cursorPos);
    }
    
    setCurrentSectionContent(newContent);
    setTableDialogOpen(false);
  };
  
  // Image dialog handlers
  const handleImageDialogOpen = () => {
    setImageDescription('');
    setSelectedImage(null);
    setImageDialogOpen(true);
  };
  
  const handleImageDialogClose = () => {
    setImageDialogOpen(false);
  };
  
  const handleImageSelection = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedImage(e.target.files[0]);
    }
  };
  
  const handleImageDescriptionChange = (e) => {
    setImageDescription(e.target.value);
  };
  
  const handleCaptureImage = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      
      // Create video and canvas elements for capturing
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();
      
      // Wait for video to be ready
      await new Promise(resolve => {
        video.onloadedmetadata = () => {
          resolve();
        };
      });
      
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        // Create a File object from the blob
        const capturedImage = new File([blob], 'captured-image.png', { type: 'image/png' });
        setSelectedImage(capturedImage);
        
        // Stop all video tracks
        stream.getTracks().forEach(track => track.stop());
      }, 'image/png');
      
    } catch (error) {
      console.error('Error capturing image:', error);
      setSnackbarMessage('Error capturing image. Please try again or upload a file instead.');
      setSnackbarOpen(true);
    }
  };
  
  const handleImageUpload = async () => {
    if (!selectedImage) {
      setSnackbarMessage('Please select an image to upload');
      setSnackbarOpen(true);
      return;
    }
    
    setImageUploading(true);
    
    try {
      const response = await uploadImage(selectedImage);
      
      if (response.success) {
        // Insert image markdown at cursor position or replace selection
        const imageMarkdown = `![${imageDescription || 'Image'}](${response.url})`;
        let newContent = currentSectionContent;
        const selection = window.getSelection().toString();
        
        if (selection) {
          newContent = currentSectionContent.replace(selection, imageMarkdown);
        } else {
          // Get cursor position from ref
          let cursorPos = 0;
          if (sectionEditorRef.current) {
            cursorPos = sectionEditorRef.current.selectionStart || 0;
          }
          
          newContent = currentSectionContent.substring(0, cursorPos) + 
                      imageMarkdown +
                      currentSectionContent.substring(cursorPos);
        }
        
        setCurrentSectionContent(newContent);
        setSnackbarMessage('Image uploaded successfully');
        setSnackbarOpen(true);
        setImageDialogOpen(false);
      } else {
        setSnackbarMessage(response.error || 'Failed to upload image');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setSnackbarMessage('Error uploading image. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setImageUploading(false);
    }
  };
  
  const handleFormatText = (formatAction) => {
    if (currentSectionIndex < 0) return;
    
    // For table and image, open the respective dialogs
    if (formatAction === 'table') {
      handleTableDialogOpen();
      handleAIToolMenuClose();
      return;
    }
    
    if (formatAction === 'image') {
      handleImageDialogOpen();
      handleAIToolMenuClose();
      return;
    }
    
    let newContent = currentSectionContent;
    const selection = window.getSelection().toString();
    
    if (selection) {
      // Format the selected text
      switch (formatAction) {
        case 'bold':
          newContent = currentSectionContent.replace(selection, `**${selection}**`);
          break;
        case 'italic':
          newContent = currentSectionContent.replace(selection, `*${selection}*`);
          break;
        case 'bullet':
          newContent = currentSectionContent.replace(selection, selection.split('\n').map(line => `- ${line}`).join('\n'));
          break;
        case 'number':
          newContent = currentSectionContent.replace(
            selection, 
            selection.split('\n').map((line, i) => `${i+1}. ${line}`).join('\n')
          );
          break;
        default:
          break;
      }
    } else {
      // No selection, add at cursor position
      let cursorPos = 0;
      
      // Get cursor position from sectionEditorRef
      if (sectionEditorRef.current) {
        cursorPos = sectionEditorRef.current.selectionStart || 0;
      }
      
      // Handle different formatting options
      switch (formatAction) {
        case 'bold':
          newContent = `${currentSectionContent.substring(0, cursorPos)}**bold text**${currentSectionContent.substring(cursorPos)}`;
          break;
        case 'italic':
          newContent = `${currentSectionContent.substring(0, cursorPos)}*italic text*${currentSectionContent.substring(cursorPos)}`;
          break;
        case 'bullet':
          newContent = `${currentSectionContent.substring(0, cursorPos)}\n- List item\n${currentSectionContent.substring(cursorPos)}`;
          break;
        case 'number':
          newContent = `${currentSectionContent.substring(0, cursorPos)}\n1. List item\n${currentSectionContent.substring(cursorPos)}`;
          break;
        default:
          break;
      }
    }
    
    setCurrentSectionContent(newContent);
    handleAIToolMenuClose();
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleAIMenuOpen = (event) => {
    setAiMenuAnchor(event.currentTarget);
  };
  
  const handleAIMenuClose = () => {
    setAiMenuAnchor(null);
  };
  
  const handleAIAction = async (action) => {
    if (currentSectionIndex < 0) return;
    
    const selectedText = window.getSelection().toString();
    const section = documentSections[currentSectionIndex];
    
    setAIProcessing(true);
    setSnackbarMessage("AI processing your request...");
    setSnackbarOpen(true);
    
    try {
      // Use functional specification AI enhancement
      const response = await enhanceSectionWithAI({
        title: section.title,
        content: currentSectionContent,
        action: action,
        project_type: formData.project_type || "SAP Implementation",
        selection: selectedText
      });
      
      if (response.success) {
        // Get content from response
        const content = response.content;
        
        if (action === 'suggest') {
          // For suggestions, show them in a panel for user to choose
          setAiSuggestions(content.split('\n').filter(line => line.trim()));
          setAiSuggestionsOpen(true);
        } else if (selectedText && ['expand', 'technical', 'technical_focus'].includes(action)) {
          // Replace selected text
          setCurrentSectionContent(currentSectionContent.replace(
            selectedText, 
            ['technical', 'technical_focus'].includes(action) 
              ? `\n\n${content}\n\n` 
              : content
          ));
        } else if (['enhance', 'refine', 'enterprise_ready'].includes(action)) {
          // Replace entire content
          setCurrentSectionContent(content);
        } else {
          // Append to the end
          setCurrentSectionContent(currentSectionContent + "\n\n" + content);
        }
        
        setSnackbarMessage(`AI ${action} completed successfully for functional specification`);
      } else {
        setSnackbarMessage(response.error || `Error with AI ${action}`);
      }
    } catch (error) {
      console.error(`Error with AI ${action}:`, error);
      setSnackbarMessage(`Error with AI ${action}. Please try again.`);
    } finally {
      setAIProcessing(false);
      setSnackbarOpen(true);
    }
  };
  
  const applySuggestion = (index) => {
    if (index >= 0 && index < aiSuggestions.length) {
      setCurrentSectionContent(currentSectionContent + "\n\n" + aiSuggestions[index]);
    }
  };
  
  const getSectionSpecificAIOptions = (sectionTitle) => {
    const sectionLower = sectionTitle.toLowerCase();
    
    if (sectionLower.includes('requirement')) {
      return [
        { label: 'Generate Additional Requirements', action: 'requirements', icon: <ListAltIcon /> },
        { label: 'Add Acceptance Criteria', action: 'criteria', icon: <CheckCircleIcon /> }
      ];
    }
    
    if (sectionLower.includes('technical') || sectionLower.includes('design')) {
      return [
        { label: 'Add SAP Configuration', action: 'config', icon: <SettingsIcon /> },
        { label: 'Generate ABAP Code', action: 'code', icon: <CodeIcon /> },
        { label: 'Create Process Diagram', action: 'diagram', icon: <AccountTreeIcon /> }
      ];
    }
    
    if (sectionLower.includes('test') || sectionLower.includes('risk')) {
      return [
        { label: 'Generate Test Scenarios', action: 'test', icon: <BugReportIcon /> },
        { label: 'Add Risk Assessment', action: 'risk', icon: <ErrorOutlineIcon /> }
      ];
    }
    
    // Default options - Fixed to use MenuBookIcon instead of ExamplesIcon
    return [
      { label: 'Enhance Content', action: 'enhance', icon: <AutoFixHighIcon /> },
      { label: 'Add Examples', action: 'examples', icon: <MenuBookIcon /> }
    ];
  };
  
  const handleDownloadPDF = async () => {
    try {
      // Get document ID from session storage
      const documentId = sessionStorage.getItem('saved_document_id');
      const projectId = sessionStorage.getItem('projectId');
      
      if (!documentId && !projectId) {
        setSnackbarMessage('Document information not found. Please save the document first.');
        setSnackbarOpen(true);
        return;
      }
      
      setSnackbarMessage('Initiating PDF download...');
      setSnackbarOpen(true);
      
      console.log(`Downloading PDF for document ID: ${documentId || 'N/A'}, Project ID: ${projectId || 'Not specified'}`);
      
      // Check if the document ID starts with 'DOC-' (solution proposal pattern)
      const isSolutionProposal = documentId?.startsWith('DOC-');
      
      // Call the enhanced API function for downloading with appropriate params
      let result;
      if (isSolutionProposal && projectId) {
        // For solution proposal documents, use the specialized path
        console.log(`Detected solution proposal document. Using project-specific endpoint.`);
        result = await downloadDocumentById(null, 'pdf', true, projectId);
      } else {
        // For regular documents, use the standard path
        console.log(`Using standard document endpoint.`);
        result = await downloadDocumentById(documentId, 'pdf');
      }
      
      if (result) {
        setSnackbarMessage('PDF download successful');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('Error downloading PDF. Please try again.');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setSnackbarMessage('Error downloading PDF. Please try again.');
      setSnackbarOpen(true);
    }
  };
  
  const handleDownloadDOCX = async () => {
    try {
      // Get document ID from session storage
      const documentId = sessionStorage.getItem('saved_document_id');
      const projectId = sessionStorage.getItem('projectId');
      
      if (!documentId && !projectId) {
        setSnackbarMessage('Document information not found. Please save the document first.');
        setSnackbarOpen(true);
        return;
      }
      
      setSnackbarMessage('Initiating DOCX download...');
      setSnackbarOpen(true);
      
      console.log(`Downloading DOCX for document ID: ${documentId || 'N/A'}, Project ID: ${projectId || 'Not specified'}`);
      
      // Check if the document ID starts with 'DOC-' (solution proposal pattern)
      const isSolutionProposal = documentId?.startsWith('DOC-');
      
      // Call the enhanced API function for downloading with appropriate params
      let result;
      if (isSolutionProposal && projectId) {
        // For solution proposal documents, use the specialized path
        console.log(`Detected solution proposal document. Using project-specific endpoint.`);
        result = await downloadDocumentById(null, 'docx', true, projectId);
      } else {
        // For regular documents, use the standard path
        console.log(`Using standard document endpoint.`);
        result = await downloadDocumentById(documentId, 'docx');
      }
      
      if (result) {
        setSnackbarMessage('DOCX download successful');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('Error downloading DOCX. Please try again.');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error downloading DOCX:', error);
      setSnackbarMessage('Error downloading DOCX. Please try again.');
      setSnackbarOpen(true);
    }
  };
  
  const handleNewDocument = () => {
    // Set flag to indicate this will be a new document
    sessionStorage.setItem('isNewDocument', 'true');
    navigate('/generate');
  };

  // Handler for project changes from DocumentProjectAccess component
  const handleProjectChange = (updatedDocument) => {
    // Update project-related information
    if (updatedDocument && updatedDocument.project_id) {
      setProjectId(updatedDocument.project_id);
      sessionStorage.setItem('projectId', updatedDocument.project_id);
      
      // Update form data if needed
      setFormData(prev => ({
        ...prev,
        project_name: updatedDocument.project_name || prev.project_name
      }));
    }
  };

  // Handler for tag changes from DocumentTags component
  const handleTagsChange = (newTags) => {
    setDocumentTags(newTags);
  };
  
  const handleSaveDocument = async () => {
    setSavingDocument(true);
    setConfirmSaveOpen(false);
    
    try {
      if (!currentUser) {
        setSnackbarMessage('You must be logged in to save documents');
        setSnackbarOpen(true);
        setSavingDocument(false);
        return;
      }
      
      // Get the document name from form data
      const documentName = formData.project_name 
        ? `${formData.project_name} - SAP Functional Specification` 
        : "SAP Functional Specification";
      
      // Get project ID from session storage if available
      const projectId = sessionStorage.getItem('projectId');
      
      const companyInfo = {
        name: formData.project_name,
        author: formData.author_name,
        date: formData.date,
        project_id: projectId // Include project ID in company info
      };
      
      // Create metadata for the document
      const metadata = {
        ...formData,
        saved_date: new Date().toISOString(),
        document_name: documentName,
        formats: ['md', 'pdf', 'docx'], // Track which formats this document has
        project_id: projectId // Include project ID in metadata
      };
      
      console.log(`Project ID for document: ${projectId || 'Not specified'}`);
      
      // Get existing document ID if available
      const existingDocumentId = sessionStorage.getItem('saved_document_id');
      
      // Check if this is a new document or an update to an existing document
      // When coming from "Generate New Document", we need to create a new document ID
      const isNewDocument = sessionStorage.getItem('isNewDocument') === 'true';
      
      console.log(`Saving document. Existing ID: ${existingDocumentId}, Is new document: ${isNewDocument}`);
      
      // First save the markdown version which will establish the document ID
      const markdownResult = await saveMarkdown(
        document, 
        documentName, 
        metadata, 
        isNewDocument ? null : existingDocumentId,  // If new document, use null to generate new ID
        isNewDocument,  // Flag indicating if this is a new document
        projectId, // Explicitly pass project ID
        'functional_spec' // Functional specification document type
      );
      
      if (!markdownResult.success) {
        throw new Error(markdownResult.error || 'Failed to save document');
      }
      
      // Get the document ID from the result (either new or existing)
      const documentId = markdownResult.document_id;
      console.log(`Document saved with ID: ${documentId}, Base ID: ${markdownResult.base_document_id || 'N/A'}`);
      
      // If the document ID changed from what was in the content, update the document
      if (documentId && document.includes('| Document ID |')) {
        const docInfoPattern = /(\| Document ID \| )([A-Za-z0-9-]+)( \|)/;
        const updatedDocument = document.replace(docInfoPattern, `$1${documentId}$3`);
        if (updatedDocument !== document) {
          console.log(`Updated document content with correct document ID: ${documentId}`);
          setDocument(updatedDocument);
          sessionStorage.setItem('generatedDocument', updatedDocument);
        }
      }
      
      // Store the document ID in session storage for later use
      if (documentId) {
        // Set the current document ID for this session
        sessionStorage.setItem('saved_document_id', documentId);
        
        // Reset the new document flag since we've now saved it
        sessionStorage.setItem('isNewDocument', 'false');
        
        // Now that we have a document ID, create PDF and DOCX formats
        // using the same document ID to link them together
        
        // Create PDF and DOCX versions in sequence, passing document ID and parent info
        // This ensures formats are properly linked
        const parentInfo = {
          parent_document_id: documentId,
          project_id: projectId, // Include project ID in parent info
          ...companyInfo
        };
        
        // Try to create both formats but continue even if one fails
        try {
          const pdfResult = await createDocument(document, 'pdf', parentInfo, documentId);
          if (pdfResult.success) {
            console.log("PDF version created successfully");
          } else {
            console.error("Error creating PDF version:", pdfResult.error);
          }
        } catch (pdfError) {
          console.error("Error creating PDF version:", pdfError);
        }
        
        try {
          const docxResult = await createDocument(document, 'docx', parentInfo, documentId);
          if (docxResult.success) {
            console.log("DOCX version created successfully");
          } else {
            console.error("Error creating DOCX version:", docxResult.error);
          }
        } catch (docxError) {
          console.error("Error creating DOCX version:", docxError);
        }
      }
      
      // Update state to allow downloading
      setDocumentSaved(true);
      sessionStorage.setItem('documentSaved', 'true');
      
      // After successful save, load the document with its default tags 
      if (isNewDocument && documentId) {
        // Load the document data to get the tags that were automatically created by the backend
        await loadDocumentTags(documentId);
      }
      
      // Show success message
      const versionText = isNewDocument ? "new document" : "new version";
      setSnackbarMessage(`Document saved successfully as ${versionText}.`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving document:', error);
      setSnackbarMessage('Error saving document. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setSavingDocument(false);
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading document...</Typography>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button 
            variant="contained" 
            onClick={handleNewDocument}
          >
            Create New Document
          </Button>
        </Box>
      </Container>
    );
  }
  
  return (
    <Box 
      sx={{ 
        minHeight: 'calc(100vh - 64px)',
        py: 5,
        background: 'linear-gradient(135deg, #121212 0%, #1e1e1e 100%)',
        backgroundSize: 'cover',
        backgroundAttachment: 'fixed',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(245, 130, 31, 0.02) 0%, rgba(0, 0, 0, 0) 50%), radial-gradient(circle at 70% 60%, rgba(29, 43, 63, 0.05) 0%, rgba(0, 0, 0, 0) 50%)',
          pointerEvents: 'none'
        }
      }}
    >
    <Container maxWidth="lg">
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 2,
          background: 'linear-gradient(135deg, #1e1e1e 0%, #252525 100%)',
          border: '1px solid rgba(245, 130, 31, 0.05)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                background: 'linear-gradient(to right, #F5821F, #F5821F88)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              SAP Functional Specification
            </Typography>
            <Tooltip title="Edit document inputs">
              <IconButton 
                color="primary" 
                onClick={handleEditDialogOpen}
                sx={{ 
                  ml: 1.5,
                  background: 'rgba(245, 130, 31, 0.1)',
                  '&:hover': {
                    background: 'rgba(245, 130, 31, 0.2)',
                  }
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              color="success"
              onClick={() => setConfirmSaveOpen(true)}
              startIcon={<SaveIcon />}
              sx={{ 
                borderColor: 'rgba(46, 125, 50, 0.5)',
                '&:hover': {
                  borderColor: '#2e7d32',
                  backgroundColor: 'rgba(46, 125, 50, 0.05)'
                }
              }}
            >
              Save Version
            </Button>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={handleDownloadDOCX}
              startIcon={<FileDownloadIcon />}
              disabled={!documentSaved}
              sx={{ 
                borderColor: 'rgba(245, 130, 31, 0.5)',
                '&:hover': {
                  borderColor: '#F5821F',
                  backgroundColor: 'rgba(245, 130, 31, 0.05)'
                },
                '&.Mui-disabled': {
                  opacity: 0.6,
                  color: 'rgba(245, 130, 31, 0.5)'
                }
              }}
            >
              DOCX
            </Button>
            <Button 
              variant="contained"
              color="primary"
              onClick={handleDownloadPDF}
              startIcon={<DescriptionIcon />}
              disabled={!documentSaved}
              sx={{ 
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(245, 130, 31, 0.2)',
                '&:hover': {
                  backgroundColor: '#f06000',
                  boxShadow: '0 6px 14px rgba(245, 130, 31, 0.25)'
                },
                '&.Mui-disabled': {
                  opacity: 0.6,
                  backgroundColor: alpha(theme.palette.primary.main, 0.3)
                }
              }}
            >
              PDF
            </Button>
          </Box>
        </Box>

                {/* Document Information with Project & Tags */}
                <Box sx={{ mb: 3 }}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.background.paper, 0.3) }}>
            <Typography variant="h6" gutterBottom>Document Information</Typography>
            
            {/* Project access information */}
            <DocumentProjectAccess 
              document={{
                id: savedDocumentId,
                document_id: savedDocumentId,
                project_id: projectId,
                project_name: formData.project_name,
                user_id: currentUser?.id
              }}
              onProjectChange={handleProjectChange}
            />
            
            {/* Document tags - only show for saved documents */}
            {savedDocumentId && (
              <DocumentTags 
                tags={documentTags} 
                documentId={savedDocumentId}
                onTagsChange={handleTagsChange}
              />
            )}
          </Paper>
        </Box>
        
        <Divider sx={{ mb: 3, opacity: 0.2 }} />

        <Tabs 
          value={viewMode} 
          onChange={handleViewModeChange}
          sx={{ 
            mb: 3,
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.primary.main,
              height: 3,
              borderRadius: '3px 3px 0 0'
            },
            '& .MuiTab-root': {
              fontSize: '0.95rem',
              fontWeight: 500,
              mx: 1,
              '&.Mui-selected': {
                color: theme.palette.primary.main
              }
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab 
            value="preview" 
            label="Document Preview" 
            icon={<DescriptionIcon />} 
            iconPosition="start"
          />
          <Tab 
            value="sections" 
            label="Edit Sections" 
            icon={<EditIcon />} 
            iconPosition="start"
          />
        </Tabs>
        
        {documentFormat === 'pdf' && viewMode === 'preview' ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="subtitle1" gutterBottom>
              Your document is ready for download in PDF format.
            </Typography>
            <Button 
              variant="contained"
              color="primary"
              onClick={handleDownloadPDF}
              sx={{ mt: 2 }}
            >
              Download PDF
            </Button>
          </Box>
        ) : viewMode === 'preview' ? (
          <Box 
            sx={{ 
              p: 3, 
              bgcolor: 'rgba(30, 30, 30, 0.6)', 
              borderRadius: '8px', 
              boxShadow: 'inset 0 0 20px rgba(0,0,0,0.2)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              maxHeight: '70vh',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(245, 130, 31, 0.3)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(245, 130, 31, 0.5)',
                }
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '4px',
              }
            }}
            className="markdown-body"
          >
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]} 
              rehypePlugins={[rehypeRaw]}
              components={TableComponents}
            >
              {document}
            </ReactMarkdown>
          </Box>
        ) : viewMode === 'sections' ? (
          <Box sx={{ mt: 2 }}>
            {documentSections.map((section, index) => (
              <Accordion key={index} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {section.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ maxHeight: '300px', overflowY: 'auto', mb: 2 }}>
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]} 
                      rehypePlugins={[rehypeRaw]}
                      components={TableComponents}
                    >
                      {section.content}
                    </ReactMarkdown>
                  </Box>
                  <Button 
                    variant="outlined" 
                    startIcon={<EditIcon />}
                    onClick={() => handleSectionEditOpen(index)}
                  >
                    Edit Section
                  </Button>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ) : viewMode === 'prompt' ? (
          <Box sx={{ 
            mt: 2, 
            p: 3, 
            bgcolor: 'rgba(30, 30, 30, 0.6)', 
            borderRadius: 1,
            border: '1px solid rgba(255, 255, 255, 0.05)'
          }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom color="primary">
              AI Model: GPT-3.5-Turbo
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              Prompt sent to OpenAI:
            </Typography>
            <Paper elevation={0} sx={{ 
              p: 2, 
              bgcolor: 'rgba(35, 35, 35, 0.7)', 
              maxHeight: '500px', 
              overflowY: 'auto',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              color: '#e0e0e0'
            }}>
              <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                {aiPrompt || "Prompt information will be available after generating or updating the document."}
              </Typography>
            </Paper>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Custom Instructions:
              </Typography>
              <Paper elevation={0} sx={{ 
                p: 2, 
                bgcolor: 'rgba(35, 35, 35, 0.7)', 
                maxHeight: '200px', 
                overflowY: 'auto',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                color: '#e0e0e0'
              }}>
                <Typography sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {formData.custom_prompt || "No custom instructions provided."}
                </Typography>
              </Paper>
            </Box>
          </Box>
        ) : null}
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button 
            variant="contained" 
            color="secondary"
            onClick={handleNewDocument}
          >
            Generate New Document
          </Button>
        </Box>
      </Paper>
      
      {/* Edit Document Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Document Information</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Project Type</InputLabel>
                <Select
                  name="project_type"
                  value={formData.project_type}
                  label="Project Type"
                  onChange={handleFormChange}
                >
                  <MenuItem value="SAP S/4HANA Implementation">SAP S/4HANA Implementation</MenuItem>
                  <MenuItem value="SAP S/4HANA Migration">SAP S/4HANA Migration</MenuItem>
                  <MenuItem value="SAP ECC Enhancement">SAP ECC Enhancement</MenuItem>
                  <MenuItem value="SAP Fiori Implementation">SAP Fiori Implementation</MenuItem>
                  <MenuItem value="SAP BW/4HANA">SAP BW/4HANA</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Project Name"
                name="project_name"
                value={formData.project_name}
                onChange={handleFormChange}
                fullWidth
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Client"
                name="client"
                value={formData.client}
                onChange={handleFormChange}
                fullWidth
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Author Name"
                name="author_name"
                value={formData.author_name}
                onChange={handleFormChange}
                fullWidth
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleFormChange}
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Custom Instructions"
                name="custom_prompt"
                value={formData.custom_prompt}
                onChange={handleFormChange}
                multiline
                rows={6}
                fullWidth
                required
                helperText="Be as specific as possible to get the best results across all sections (Section 4-16)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateDocument} 
            color="primary" 
            variant="contained"
            disabled={updatingDocument}
          >
            {updatingDocument ? <CircularProgress size={24} /> : 'Update Document'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Section Edit Dialog */}
      <Dialog
        open={sectionEditDialogOpen}
        onClose={handleSectionEditClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' }
        }}
      >
        {currentSectionIndex >= 0 && (
          <>
            <AppBar position="relative" color="default" elevation={0}>
              <Toolbar>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Edit Section: {documentSections[currentSectionIndex]?.title}
                </Typography>
                <Box>
                  <Tooltip title="Bold">
                    <IconButton onClick={() => handleFormatText('bold')}>
                      <FormatBoldIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Italic">
                    <IconButton onClick={() => handleFormatText('italic')}>
                      <FormatItalicIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Bullet List">
                    <IconButton onClick={() => handleFormatText('bullet')}>
                      <FormatListBulletedIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Numbered List">
                    <IconButton onClick={() => handleFormatText('number')}>
                      <FormatListNumberedIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Insert Table">
                    <IconButton onClick={() => handleFormatText('table')}>
                      <TableChartIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Insert Image">
                    <IconButton onClick={() => handleFormatText('image')}>
                      <InsertPhotoIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="AI Assistant">
                    <IconButton 
                      onClick={handleAIMenuOpen}
                      color="primary"
                      sx={{ 
                        ml: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                        }
                      }}
                    >
                      <AutoAwesomeIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="AI Editing Tools">
                    <IconButton onClick={handleAIToolMenuOpen}>
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Toolbar>
            </AppBar>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
              <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Edit Content
                  </Typography>
                  <TextField
                    inputRef={sectionEditorRef}
                    value={currentSectionContent}
                    onChange={handleSectionContentChange}
                    multiline
                    fullWidth
                    variant="outlined"
                    rows={20}
                    sx={{ fontFamily: 'monospace' }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Preview
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      height: '100%', 
                      overflowY: 'auto',
                      bgcolor: 'rgba(30, 30, 30, 0.8)',
                      color: '#e0e0e0',
                      border: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]} 
                      rehypePlugins={[rehypeRaw]}
                      components={TableComponents}
                    >
                      {currentSectionContent}
                    </ReactMarkdown>
                  </Paper>
                </Grid>
              </Grid>
              {/* AI Suggestions Panel */}
              <Collapse in={aiSuggestionsOpen}>
                <Paper
                  elevation={3}
                  sx={{
                    mt: 2,
                    p: 2,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    borderRadius: 1,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <TipsAndUpdatesIcon color="primary" sx={{ mr: 1 }} />
                      AI Suggestions
                    </Typography>
                    <IconButton size="small" onClick={() => setAiSuggestionsOpen(false)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  {aiSuggestions.length > 0 ? (
                    <List dense>
                      {aiSuggestions.map((suggestion, index) => (
                        <ListItem 
                          key={index}
                          secondaryAction={
                            <IconButton edge="end" onClick={() => applySuggestion(index)}>
                              <AddCircleOutlineIcon color="primary" />
                            </IconButton>
                          }
                          sx={{ 
                            bgcolor: 'background.paper',
                            mb: 1,
                            borderRadius: 1,
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                          }}
                        >
                          <ListItemText 
                            primary={suggestion} 
                            primaryTypographyProps={{ 
                              variant: 'body2',
                              sx: { 
                                mr: 6,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }
                            }} 
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No suggestions available. Try generating some using the AI Assistant.
                    </Typography>
                  )}
                </Paper>
              </Collapse>
              {/* AI Processing Overlay */}
              {aiProcessing && (
                <Box sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  bottom: 0, 
                  backgroundColor: 'rgba(0, 0, 0, 0.7)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: 1
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress color="primary" size={60} thickness={4} />
                    <Typography sx={{ mt: 3, color: 'white', fontWeight: 500 }}>
                      AI is processing your request...
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: '#ccc' }}>
                      This may take a few seconds
                    </Typography>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleSectionEditClose} color="primary">
                Cancel
              </Button>
              <Button 
                onClick={handleSectionSave} 
                color="primary" 
                variant="contained"
              >
                Save Section
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      {/* AI Assistant Menu */}
      <Menu
        anchorEl={aiMenuAnchor}
        open={Boolean(aiMenuAnchor)}
        onClose={handleAIMenuClose}
        PaperProps={{
          sx: { 
            width: 280, // Slightly wider to accommodate the info icon
            maxHeight: 350,
            mt: 1.5,
            overflow: 'auto'
          }
        }}
      >
        {/* Menu items with info icons */}
        <MenuItem onClick={() => { handleAIAction('enhance'); handleAIMenuClose(); }}>
          <ListItemIcon><AutoFixHighIcon color="primary" /></ListItemIcon>
          <ListItemText 
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Enhance Section
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>{aiFeatureInfo.enhance.title}</Typography>
                      <Typography variant="body2" paragraph>{aiFeatureInfo.enhance.description}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>When to use:</Typography>
                      <Typography variant="body2" paragraph>{aiFeatureInfo.enhance.usage}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>What to expect:</Typography>
                      <Typography variant="body2">{aiFeatureInfo.enhance.expectation}</Typography>
                    </Box>
                  }
                  placement="right"
                  arrow
                >
                  <IconButton size="small" sx={{ ml: 1, p: 0.25 }}>
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Box>
            } 
            secondary="Improve quality & details" 
          />
        </MenuItem>
        
        <MenuItem onClick={() => { handleAIAction('suggest'); handleAIMenuClose(); }}>
          <ListItemIcon><TipsAndUpdatesIcon color="primary" /></ListItemIcon>
          <ListItemText 
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                AI Suggestions
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>{aiFeatureInfo.suggest.title}</Typography>
                      <Typography variant="body2" paragraph>{aiFeatureInfo.suggest.description}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>When to use:</Typography>
                      <Typography variant="body2" paragraph>{aiFeatureInfo.suggest.usage}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>What to expect:</Typography>
                      <Typography variant="body2">{aiFeatureInfo.suggest.expectation}</Typography>
                    </Box>
                  }
                  placement="right"
                  arrow
                >
                  <IconButton size="small" sx={{ ml: 1, p: 0.25 }}>
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Box>
            } 
            secondary="Get content recommendations" 
          />
        </MenuItem>
        
        <Divider />
        
        <ListSubheader>Section-Specific Actions</ListSubheader>
        
        {currentSectionIndex >= 0 && getSectionSpecificAIOptions(documentSections[currentSectionIndex].title).map((option, index) => {
          const info = aiFeatureInfo[option.action];
          return (
            <MenuItem key={index} onClick={() => { handleAIAction(option.action); handleAIMenuClose(); }}>
              <ListItemIcon>{option.icon}</ListItemIcon>
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {option.label}
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>{info?.title}</Typography>
                          <Typography variant="body2" paragraph>{info?.description}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>When to use:</Typography>
                          <Typography variant="body2" paragraph>{info?.usage}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>What to expect:</Typography>
                          <Typography variant="body2">{info?.expectation}</Typography>
                        </Box>
                      }
                      placement="right"
                      arrow
                    >
                      <IconButton size="small" sx={{ ml: 1, p: 0.25 }}>
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                } 
              />
            </MenuItem>
          );
        })}
        
        <Divider />
        
        {/* Functional Specification AI options */}
        <MenuItem onClick={() => { handleAIAction('technical'); handleAIMenuClose(); }}>
          <ListItemIcon><CodeIcon color="primary" /></ListItemIcon>
          <ListItemText 
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Generate Technical Content
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>{aiFeatureInfo.technical.title}</Typography>
                      <Typography variant="body2" paragraph>{aiFeatureInfo.technical.description}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>When to use:</Typography>
                      <Typography variant="body2" paragraph>{aiFeatureInfo.technical.usage}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>What to expect:</Typography>
                      <Typography variant="body2">{aiFeatureInfo.technical.expectation}</Typography>
                    </Box>
                  }
                  placement="right"
                  arrow
                >
                  <IconButton size="small" sx={{ ml: 1, p: 0.25 }}>
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Box>
            } 
            secondary="Add SAP-specific details" 
          />
        </MenuItem>
        
        <MenuItem onClick={() => { handleAIAction('expand'); handleAIMenuClose(); }}>
          <ListItemIcon><ZoomOutMapIcon color="primary" /></ListItemIcon>
          <ListItemText 
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Expand Selected Text
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>{aiFeatureInfo.expand.title}</Typography>
                      <Typography variant="body2" paragraph>{aiFeatureInfo.expand.description}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>When to use:</Typography>
                      <Typography variant="body2" paragraph>{aiFeatureInfo.expand.usage}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>What to expect:</Typography>
                      <Typography variant="body2">{aiFeatureInfo.expand.expectation}</Typography>
                    </Box>
                  }
                  placement="right"
                  arrow
                >
                  <IconButton size="small" sx={{ ml: 1, p: 0.25 }}>
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Box>
            } 
            secondary="Elaborate on selection" 
          />
        </MenuItem>
      </Menu>
      {/* AI Tools Menu */}
      <Menu
        anchorEl={aiToolMenuAnchor}
        open={Boolean(aiToolMenuAnchor)}
        onClose={handleAIToolMenuClose}
      >
        <ListItem button onClick={() => {
          handleAIToolMenuClose();
          setSnackbarMessage('Background image feature coming soon!');
          setSnackbarOpen(true);
        }}>
          <ListItemIcon>
            <InsertPhotoIcon />
          </ListItemIcon>
          <ListItemText primary="Upload Background Image" />
        </ListItem>
        <ListItem button onClick={() => {
          handleAIToolMenuClose();
          setSnackbarMessage('Attach file feature coming soon!');
          setSnackbarOpen(true);
        }}>
          <ListItemIcon>
            <AttachFileIcon />
          </ListItemIcon>
          <ListItemText primary="Attach File" />
        </ListItem>
        <ListItem button onClick={() => {
          handleAIToolMenuClose();
          setSnackbarMessage('Chart generation feature coming soon!');
          setSnackbarOpen(true);
        }}>
          <ListItemIcon>
            <TableChartIcon />
          </ListItemIcon>
          <ListItemText primary="Generate Chart" />
        </ListItem>
      </Menu>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
      
      {/* Table Creator Dialog */}
      <Dialog
        open={tableDialogOpen}
        onClose={handleTableDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Create Table</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Rows"
                  name="rows"
                  type="number"
                  value={rows}
                  onChange={handleRowColumnChange}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Columns"
                  name="columns"
                  type="number"
                  value={columns}
                  onChange={handleRowColumnChange}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Table Style</InputLabel>
                  <Select
                    value={tableStyle}
                    onChange={handleTableStyleChange}
                    label="Table Style"
                  >
                    <MenuItem value="default">Default</MenuItem>
                    <MenuItem value="striped">Striped</MenuItem>
                    <MenuItem value="bordered">Bordered</MenuItem>
                    <MenuItem value="condensed">Condensed</MenuItem>
                    <MenuItem value="hover">Hover Effect</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Button 
              variant="outlined" 
              startIcon={<AddIcon />} 
              onClick={handleAddRow}
              size="small"
            >
              Add Row
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<AddIcon />} 
              onClick={handleAddColumn}
              size="small"
            >
              Add Column
            </Button>
          </Box>
          
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 2 }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ width: '40px' }}></Box>
                  {tableHeaders.map((header, index) => (
                    <Box 
                      key={index} 
                      component="th"
                      sx={{ 
                        border: '1px solid #ddd', 
                        padding: '8px',
                        backgroundColor: '#f5f5f5',
                        position: 'relative'
                      }}
                    >
                      <TextField
                        value={header}
                        onChange={(e) => handleHeaderChange(index, e.target.value)}
                        placeholder={`Header ${index + 1}`}
                        variant="standard"
                        fullWidth
                      />
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteColumn(index)}
                        sx={{ 
                          position: 'absolute', 
                          top: '-10px', 
                          right: '-10px', 
                          bgcolor: '#f5f5f5',
                          '&:hover': { bgcolor: '#e0e0e0' }
                        }}
                      >
                        <Box component="span" sx={{ fontSize: '16px', fontWeight: 'bold' }}>×</Box>
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {tableContent.map((row, rowIndex) => (
                  <Box component="tr" key={rowIndex}>
                    <Box 
                      component="td" 
                      sx={{ 
                        width: '40px',
                        position: 'relative'
                      }}
                    >
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteRow(rowIndex)}
                        sx={{ 
                          position: 'absolute', 
                          top: '50%', 
                          left: '50%', 
                          transform: 'translate(-50%, -50%)',
                          bgcolor: '#f5f5f5',
                          '&:hover': { bgcolor: '#e0e0e0' }
                        }}
                      >
                        <Box component="span" sx={{ fontSize: '16px', fontWeight: 'bold' }}>×</Box>
                      </IconButton>
                    </Box>
                    {row.map((cell, colIndex) => (
                      <Box 
                        key={colIndex} 
                        component="td" 
                        sx={{ 
                          border: '1px solid #ddd', 
                          padding: '8px' 
                        }}
                      >
                        <TextField
                          value={cell}
                          onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                          placeholder={`Row ${rowIndex + 1}, Col ${colIndex + 1}`}
                          variant="standard"
                          fullWidth
                        />
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Preview:</Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                bgcolor: 'rgba(30, 30, 30, 0.8)',
                color: '#e0e0e0',
                border: '1px solid rgba(255, 255, 255, 0.05)'
              }}
            >
              <Box component="table" sx={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                borderSpacing: 0,
                color: '#e0e0e0',
                ...(tableStyle === 'striped' && {
                  '& tbody tr:nth-of-type(odd)': {
                    backgroundColor: 'rgba(45, 45, 45, 0.5)'
                  }
                }),
                ...(tableStyle === 'bordered' && {
                  '& th, & td': {
                    border: '2px solid rgba(255, 255, 255, 0.2)'
                  }
                }),
                ...(tableStyle === 'condensed' && {
                  '& th, & td': {
                    padding: '4px'
                  }
                }),
                ...(tableStyle === 'hover' && {
                  '& tbody tr:hover': {
                    backgroundColor: 'rgba(245, 130, 31, 0.1)'
                  }
                })
              }}>
                <Box component="thead">
                  <Box component="tr">
                    {tableHeaders.map((header, index) => (
                      <Box 
                        key={index} 
                        component="th" 
                        sx={{ 
                          border: '1px solid rgba(255, 255, 255, 0.2)', 
                          padding: '8px',
                          backgroundColor: '#1D2B3F',
                          color: '#F5821F',
                          fontWeight: 'bold',
                          textAlign: 'left'
                        }}
                      >
                        {header || `Header ${index + 1}`}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {tableContent.map((row, rowIndex) => (
                    <Box component="tr" key={rowIndex}
                      sx={{
                        backgroundColor: rowIndex % 2 === 0 ? 'rgba(35, 35, 35, 0.5)' : 'rgba(45, 45, 45, 0.5)', 
                        '&:hover': { backgroundColor: 'rgba(245, 130, 31, 0.1)' }
                      }}
                    >
                      {row.map((cell, colIndex) => (
                        <Box 
                          key={colIndex} 
                          component="td" 
                          sx={{ 
                            border: '1px solid rgba(255, 255, 255, 0.1)', 
                            padding: '8px',
                            color: '#e0e0e0'
                          }}
                        >
                          {cell || `Cell ${rowIndex + 1},${colIndex + 1}`}
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTableDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleInsertTable} color="primary" variant="contained">
            Insert Table
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Image Upload Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={handleImageDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Image</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2, mt: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Option 1: Upload from device
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<InsertPhotoIcon />}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  Choose File
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageSelection}
                  />
                </Button>
                
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
                  Option 2: Capture from camera
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleCaptureImage}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  Take Photo
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              {selectedImage ? (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Selected: {selectedImage.name}
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 1, 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      height: '200px'
                    }}
                  >
                    <Box
                      component="img"
                      src={URL.createObjectURL(selectedImage)}
                      alt="Preview"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '180px',
                        objectFit: 'contain'
                      }}
                    />
                  </Paper>
                </Box>
              ) : (
                <Box 
                  sx={{ 
                    height: '200px', 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    border: '1px dashed #ccc',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Image preview will appear here
                  </Typography>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Image Description/Caption"
                value={imageDescription}
                onChange={handleImageDescriptionChange}
                fullWidth
                placeholder="Enter a description for the image"
                helperText="This will be used for accessibility and will appear as caption below the image"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Note:</strong> Images will be stored on the server and included in the final document export.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImageDialogClose} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleImageUpload} 
            color="primary" 
            variant="contained"
            disabled={!selectedImage || imageUploading}
          >
            {imageUploading ? <CircularProgress size={24} /> : 'Upload & Insert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Confirmation Dialog */}
      <Dialog
        open={confirmSaveOpen}
        onClose={() => setConfirmSaveOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to save this document as a new version? This will create PDF and DOCX versions for download.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSaveOpen(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleSaveDocument} 
            color="primary" 
            variant="contained"
            disabled={savingDocument}
          >
            {savingDocument ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </Box>
  );
};

export default ViewDocumentPage;